import { computeProposalTotal, ChargeableItemLike } from './proposalMath';

/**
 * PREÇO × VALOR ESTIMADO na montagem da proposta (Heitor, 21/10/2026, M1 e M2 do Artifact da Montagem).
 *
 * A conta que se fazia de olho, linha a linha, comparando "Preco Unit." com a coluna "Valor Est." ao lado. Aqui ela
 * sai pronta: por item (quanto este preço está do estimado) e no total (quanto a proposta inteira está do total
 * estimado do órgão).
 *
 * Só compara o que tem base. Item sigiloso ou sem estimado não tem estimado para comparar; no total, um item assim
 * deixaria o "estimado" menor que o real e a proposta pareceria ACIMA do órgão sem estar. Por isso o total só se
 * compara quando todo item tem estimado, e diz quantos faltam quando não tem.
 */

type ItemComEstimado = ChargeableItemLike & { estimatedPrice?: number | string; valorEstimadoSigiloso?: boolean };

export type Distancia = { tipo: 'igual' | 'abaixo' | 'acima'; pct: number };

const temEstimado = (it: ItemComEstimado) => !it.valorEstimadoSigiloso && Number(it.estimatedPrice) > 0;

/** Distância relativa de `valor` para `base`, em %; igual quando a diferença some na 4ª casa do unitário. */
function distancia(valor: number, base: number, tolerancia: number): Distancia {
  if (Math.abs(valor - base) < tolerancia) return { tipo: 'igual', pct: 0 };
  const pct = Math.abs((valor - base) / base) * 100;
  return { tipo: valor < base ? 'abaixo' : 'acima', pct };
}

/** Quanto o preço unitário do item está do estimado; null sem preço ou sem estimado. */
export function distanciaDoEstimado(it: ItemComEstimado): Distancia | null {
  if (!temEstimado(it)) return null;
  const preco = parseFloat(String(it.unitPrice));
  if (!Number.isFinite(preco) || String(it.unitPrice).trim() === '') return null;
  return distancia(preco, Number(it.estimatedPrice), 0.00005);
}

/**
 * Total estimado da proposta, com a MESMA aritmética do total (meses, taxa única), trocando o preço pelo estimado.
 * `semEstimado` > 0: não há base para comparar o total, e `distancia` fica null.
 */
export function totalEstimado(items: ItemComEstimado[], contractMonths: number, total: number): {
  estimado: number; semEstimado: number; distancia: Distancia | null;
} {
  const semEstimado = items.filter((it) => !temEstimado(it)).length;
  const estimado = computeProposalTotal(
    items.filter(temEstimado).map((it) => ({ ...it, unitPrice: Number(it.estimatedPrice) })),
    contractMonths,
  );
  return {
    estimado,
    semEstimado,
    distancia: semEstimado === 0 && estimado > 0 && total > 0 ? distancia(total, estimado, 0.005) : null,
  };
}

/** "8,2%" — uma casa; duas quando a primeira arredondaria para zero. */
export function fmtPct(pct: number): string {
  const casas = pct > 0 && pct < 0.1 ? 2 : 1;
  return `${pct.toLocaleString('pt-BR', { minimumFractionDigits: casas, maximumFractionDigits: casas })}%`;
}

/** "= estimado", "8,2% abaixo do est.", "3,0% acima do est." */
export function fraseDaDistancia(d: Distancia): string {
  if (d.tipo === 'igual') return '= estimado';
  return `${fmtPct(d.pct)} ${d.tipo} do est.`;
}
